import { TalentFilters, serializeTalentFilters, talentFiltersActiveCount } from "../Types/TalentIntelligenceTypes";

export interface PersistedIntelligenceSearch {
  id: string;
  name?: string;
  term: string;
  filters: TalentFilters;
  filterCount: number;
  createdAt: number;
}

const HISTORY_KEY = "nexgent.intel.searchHistory";
const SAVED_KEY = "nexgent.intel.savedSearches";
const HISTORY_LIMIT = 12;

const uid = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const read = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
};

const write = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error("Failed to persist intelligence searches", err);
  }
};

const fingerprintOf = (term: string, filters: TalentFilters): string =>
  JSON.stringify({ term: (term || "").trim().toLowerCase(), filters: serializeTalentFilters(filters) });

const buildEntry = (
  seed: { term?: string; filters: TalentFilters; name?: string },
  filterCount: number
): PersistedIntelligenceSearch => ({
  id: uid(),
  name: seed.name,
  term: seed.term || "",
  filters: { ...seed.filters },
  filterCount,
  createdAt: Date.now(),
});

export const loadSearchHistory = (): PersistedIntelligenceSearch[] =>
  read<PersistedIntelligenceSearch[]>(HISTORY_KEY, []);

export const recordSearch = (seed: { term?: string; filters: TalentFilters }): PersistedIntelligenceSearch[] => {
  const list = loadSearchHistory();
  const fingerprint = fingerprintOf(seed.term || "", seed.filters);
  const deduped = list.filter((entry) => fingerprintOf(entry.term, entry.filters) !== fingerprint);
  const next = [buildEntry(seed, talentFiltersActiveCount(seed.filters)), ...deduped].slice(0, HISTORY_LIMIT);
  write(HISTORY_KEY, next);
  return next;
};

export const deleteSearchHistoryEntry = (id: string): PersistedIntelligenceSearch[] => {
  const next = loadSearchHistory().filter((entry) => entry.id !== id);
  write(HISTORY_KEY, next);
  return next;
};

export const clearSearchHistory = (): PersistedIntelligenceSearch[] => {
  write(HISTORY_KEY, []);
  return [];
};

export const loadSavedSearches = (): PersistedIntelligenceSearch[] =>
  read<PersistedIntelligenceSearch[]>(SAVED_KEY, []);

export const saveSavedSearch = (name: string, seed: { term?: string; filters: TalentFilters }): PersistedIntelligenceSearch[] => {
  const list = loadSavedSearches();
  const next = [buildEntry({ ...seed, name }, talentFiltersActiveCount(seed.filters)), ...list];
  write(SAVED_KEY, next);
  return next;
};

export const deleteSavedSearch = (id: string): PersistedIntelligenceSearch[] => {
  const next = loadSavedSearches().filter((entry) => entry.id !== id);
  write(SAVED_KEY, next);
  return next;
};

export const formatRelativeTime = (timestamp: number): string => {
  const diffMs = Date.now() - timestamp;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};